import { useState } from "react";
import "./AccountSettings.css";
import Header from "../../Components/Shared/Header/Header";
import Footer from "../../Components/Shared/Footer/Footer";
import VivahApi from "../../api";
import authService from "../../services/auth.service";
import chatWebSocket from "../../services/chatWebSocket.service";

function ChangePasswordSection() {
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  function toggle(field) { setShow(s => ({ ...s, [field]: !s[field] })); }

  async function handleSubmit(e) {
    e.preventDefault();
    setStatus(null);
    if (form.next !== form.confirm) {
      setStatus({ type: 'error', text: 'New passwords do not match.' });
      return;
    }
    if (form.next.length < 8) {
      setStatus({ type: 'error', text: 'New password must be at least 8 characters.' });
      return;
    }
    setLoading(true);
    try {
      await VivahApi.Auth.changePassword(form.current, form.next);
      setStatus({ type: 'success', text: 'Password changed successfully.' });
      setForm({ current: '', next: '', confirm: '' });
    } catch (err) {
      setStatus({ type: 'error', text: err?.response?.data?.error || err.message || 'Failed to change password.' });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="as-card">
      <div className="as-card-header">
        <div className="as-card-icon as-icon-lock">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <rect x="5" y="11" width="14" height="10" rx="2" stroke="#e50d7e" strokeWidth="1.8"/>
            <path d="M8 11V7a4 4 0 1 1 8 0v4" stroke="#e50d7e" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </div>
        <div>
          <h3 className="as-card-title">Change Password</h3>
          <p className="as-card-subtitle">Update your account password</p>
        </div>
      </div>

      {status && (
        <div className={`as-alert as-alert-${status.type}`}>{status.text}</div>
      )}

      <form className="as-form" onSubmit={handleSubmit}>
        <div className="as-field">
          <label>Current Password</label>
          <div className="as-input-wrap">
            <input
              type={show.current ? 'text' : 'password'}
              value={form.current}
              onChange={e => setForm(f => ({ ...f, current: e.target.value }))}
              placeholder="Enter current password"
              required
              autoComplete="current-password"
            />
            <button type="button" className="as-eye" onClick={() => toggle('current')}>
              {show.current ? '🙈' : '👁️'}
            </button>
          </div>
        </div>

        <div className="as-field">
          <label>New Password</label>
          <div className="as-input-wrap">
            <input
              type={show.next ? 'text' : 'password'}
              value={form.next}
              onChange={e => setForm(f => ({ ...f, next: e.target.value }))}
              placeholder="Min. 8 characters"
              required
              autoComplete="new-password"
            />
            <button type="button" className="as-eye" onClick={() => toggle('next')}>
              {show.next ? '🙈' : '👁️'}
            </button>
          </div>
        </div>

        <div className="as-field">
          <label>Confirm New Password</label>
          <div className="as-input-wrap">
            <input
              type={show.confirm ? 'text' : 'password'}
              value={form.confirm}
              onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
              placeholder="Repeat new password"
              required
              autoComplete="new-password"
            />
            <button type="button" className="as-eye" onClick={() => toggle('confirm')}>
              {show.confirm ? '🙈' : '👁️'}
            </button>
          </div>
        </div>

        <button className="as-btn as-btn-primary" type="submit" disabled={loading}>
          {loading ? 'Updating…' : 'Update Password'}
        </button>
      </form>
    </div>
  );
}

function DeactivateSection() {
  const [reason, setReason] = useState('');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const REASONS = [
    'Found my match on Vivah Bandh',
    'Found my match on another website',
    'Found my match offline',
    'Not interested anymore',
  ];

  async function handleSubmit(e) {
    e.preventDefault();
    if (!reason) { setStatus({ type: 'error', text: 'Please select a reason.' }); return; }
    if (!window.confirm('Are you sure you want to deactivate your account? You will lose access immediately.')) return;
    setLoading(true);
    setStatus(null);
    try {
      await VivahApi.profile.deactivateProfile({ reason });
      chatWebSocket.disconnect();
      authService.logout();
      localStorage.clear();
      window.location.href = '/login';
    } catch (err) {
      setStatus({ type: 'error', text: err?.response?.data?.error || err.message || 'Failed to deactivate account.' });
      setLoading(false);
    }
  }

  return (
    <div className="as-card as-card-danger">
      <div className="as-card-header">
        <div className="as-card-icon as-icon-danger">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="9" stroke="#c62828" strokeWidth="1.8"/>
            <path d="M12 8v5" stroke="#c62828" strokeWidth="1.8" strokeLinecap="round"/>
            <circle cx="12" cy="16" r="1" fill="#c62828"/>
          </svg>
        </div>
        <div>
          <h3 className="as-card-title as-title-danger">Deactivate Account</h3>
          <p className="as-card-subtitle">You will lose access to your account immediately.</p>
        </div>
      </div>

      {status && (
        <div className={`as-alert as-alert-${status.type}`}>{status.text}</div>
      )}

      <form className="as-form" onSubmit={handleSubmit}>
        <div className="as-field">
          <label>Reason for leaving</label>
          <div className="as-radio-group">
            {REASONS.map(r => (
              <label key={r} className="as-radio-label">
                <input
                  type="radio"
                  name="reason"
                  value={r}
                  checked={reason === r}
                  onChange={() => setReason(r)}
                />
                {r}
              </label>
            ))}
          </div>
        </div>

        <button className="as-btn as-btn-danger" type="submit" disabled={loading}>
          {loading ? 'Deactivating…' : 'Deactivate Account'}
        </button>
      </form>
    </div>
  );
}

function AccountSettings() {
  return (
    <div className="gla_page" id="gla_page">
      <a href="#gla_page" className="gla_top ti ti-angle-up gla_go"></a>
      <Header />
      <div className="gla_page_title gla_image_bck gla_wht_txt" data-color="#282828">
        <div className="container text-left">
          <div className="row">
            <div className="col-md-8">
              <h1 className="gla_h1_title">Account Settings</h1>
            </div>
            <div className="col-md-4">
              <div className="breadcrumbs">
                <a href="#">Home</a>
                <span>Account Settings</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section id="gla_content" className="gla_content">
        <section className="gla_section">
          <div className="container">
            <div className="as-page">
              <ChangePasswordSection />
              <DeactivateSection />
            </div>
          </div>
        </section>
      </section>
      <Footer />
    </div>
  );
}

export default AccountSettings;
